import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, IconCollection, IconSpinner } from '../../../components';
import { getProviderEventDashboardTransactions } from '../../../util/api';
import AdjustedBillActivity from '../../../components/AdjustedBill/AdjustedBillActivity';
import EventTimeline from '../../EventDashboardPage/EventDetailsTab/EventTimeline';
import { getEventTypeLabel } from '../../../util/data';
import { parse } from '../../../util/urlHelpers';
import PaginationLinks from '../../../components/PaginationLinks/PaginationLinks';

import css from '../../EventDashboardPage/EventDashboardPageForm.module.css';

const PER_PAGE = 10;

const ProviderEventDetailsTab = props => {
  const { currentUserId } = props;

  const history = useHistory();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTransaction, setExpandedTransaction] = useState(null);

  const [meta, setMeta] = useState({ totalItems: 0, totalPages: 1, page: 1, perPage: PER_PAGE });


  const location = history.location || {};
  const queryParams = parse(location.search || '');
  const currentPage = queryParams.page && Number(queryParams.page) > 0 ? Number(queryParams.page) : 1;

  useEffect(() => {
    let cancelled = false;

    const fetchTransactions = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getProviderEventDashboardTransactions({
          userId: currentUserId,
          page: currentPage,
          perPage: PER_PAGE,
        });

        if (cancelled) return;

        if (response.error) {
          setError(response.error);
        } else {
          setTransactions(response.data?.transactions || []);
          setMeta(
            response.data?.meta || { totalItems: 0, totalPages: 1, page: currentPage, perPage: PER_PAGE }
          );
        }
      } catch (err) {
        if (cancelled) return;
        setError('Failed to fetch transactions');
        console.error('Error fetching transactions:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTransactions();
    return () => { cancelled = true; };
  }, [currentUserId, currentPage]);

  const totalPages = typeof meta?.totalPages === 'number' && meta.totalPages > 0 ? meta.totalPages : 1;
  const shouldShowPagination = totalPages > 1;
  const pagination = { page: currentPage, perPage: PER_PAGE, totalPages, totalItems: meta?.totalItems || 0 };
  // Prefer the API's total, but fall back to the count of loaded transactions
  // so the header never shows "0 Transactions" while rows are visible.
  const transactionCount = meta?.totalItems || transactions.length;

  const handleTransactionClick = transaction => {
    setExpandedTransaction(expandedTransaction?.transactionId === transaction.transactionId ? null : transaction);
  };

  const handleViewAdjustedBill = (transactionId, txEntity) => {
    const customerUuid = txEntity?.relationships?.customer?.data?.id?.uuid;
    const providerUuid = txEntity?.relationships?.provider?.data?.id?.uuid;
    const isCustomerOnTx = customerUuid && customerUuid === currentUserId;
    const isProviderOnTx = providerUuid && providerUuid === currentUserId;
    const path =
      isCustomerOnTx && !isProviderOnTx
        ? `/order/${transactionId}`
        : `/sale/${transactionId}`;
    history.push(path);
  };

  const formatEventDate = eventDetails => {
    if (!eventDetails || !eventDetails[0]) return 'N/A';
    return eventDetails[0].date || 'N/A';
  };

  const formatEventTime = eventDetails => {
    if (!eventDetails || !eventDetails[0]) return 'N/A';
    return eventDetails[0].photoBoothStartTime || 'N/A';
  };

  const getCustomerName = transaction => {
    // We only have listing.author for sure in current formatting. If you later add included customer publicData,
    // this can be improved to read from tx relationships.
    return (
      transaction.author?.attributes?.profile?.displayName ||
      transaction.author?.attributes?.profile?.publicData?.companyName ||
      '-'
    );
  };

  // Map the raw transaction state to a status category. This drives the pill
  // colour and is kept in sync with the owner-side EventDetailsTab so both
  // dashboards reflect the same true status.
  const getTransactionStatus = txEntity => {
    const state = txEntity?.attributes?.state;
    switch (state) {
      case 'state/offer-pending':
      case 'state/customer-offer-pending':
      case 'state/update-pending':
      case 'state/pending-payment':
        return 'pending';
      case 'state/offer-accepted':
        return 'accepted';
      case 'state/reduction-accepted':
        return 'creditAgreed';
      case 'state/offer-rejected':
      case 'state/payment-expired':
      case 'state/canceled':
        return 'rejected';
      case 'state/delivered':
      case 'state/reviewed-by-customer':
      case 'state/reviewed-by-provider':
      case 'state/completed':
      case 'state/reviewed':
        return 'completed';
      default:
        return 'pending';
    }
  };

  // Human-readable label for the status pill. Mirrors the owner-side labels so
  // the two dashboards use a shared vocabulary.
  const getTransactionStatusLabel = txEntity => {
    const state = txEntity?.attributes?.state;
    const invoiceType = getInvoiceType(txEntity);
    switch (state) {
      case 'state/offer-pending':
        return invoiceType === 'reduction' ? 'Reduction Pending' : 'Action Required';
      case 'state/customer-offer-pending':
        return 'Counter Sent';
      case 'state/update-pending':
        return 'Pending';
      case 'state/reduction-accepted':
        return 'Credit Agreed';
      case 'state/offer-rejected':
        return 'Rejected';
      case 'state/offer-accepted':
        return 'Accepted';
      case 'state/pending-payment':
        return 'Payment Pending';
      case 'state/payment-expired':
        return 'Payment Expired';
      case 'state/canceled':
        return 'Cancelled';
      case 'state/delivered':
      case 'state/completed':
      case 'state/reviewed':
      case 'state/reviewed-by-customer':
      case 'state/reviewed-by-provider':
        return 'Completed';
      default:
        return 'Pending';
    }
  };

  // Resolve the invoice type (overtime / expense / reduction) from either data shape.
  const getInvoiceType = txEntity => {
    const pdA = txEntity?.attributes?.protectedData || {};
    const pdB = txEntity?.protectedData || {};
    const invoiceType =
      pdA?.invoiceData?.invoiceType ||
      pdB?.invoiceData?.invoiceType ||
      pdA?.invoiceType ||
      pdB?.invoiceType;
    if (invoiceType) return invoiceType;
    if (pdA?.isOvertimeInvoice || pdB?.isOvertimeInvoice) return 'overtime';
    if (pdA?.isExpenseInvoice || pdB?.isExpenseInvoice) return 'expense';
    if (pdA?.isReductionInvoice || pdB?.isReductionInvoice) return 'reduction';
    if (pdA?.invoiceData?.overtimeDetails || pdB?.invoiceData?.overtimeDetails) return 'overtime';
    if (pdA?.invoiceData?.reductionDetails || pdB?.invoiceData?.reductionDetails) return 'reduction';
    return null;
  };

  const isInvoiceAdjustment = txEntity => !!getInvoiceType(txEntity);

  const getOvertimeRateLabel = item => {
    const listingPublicData = item?.listing?.attributes?.publicData || {};
    const overtimeRateFromListing =
      listingPublicData?.overtimeRate || listingPublicData?.overtimeDetails?.overtimeRate || null;
    const overtimeRate =
      overtimeRateFromListing || item?.protectedData?.invoiceData?.overtimeDetails?.overtimeRate;

    const currency = overtimeRate?.currency;
    const amount = overtimeRate?.amount;
    if (typeof amount !== 'number') return 'N/A';

    const value = (amount / 100).toFixed(2);
    return currency ? `${currency} ${value}` : `$${value}`;
  };

  const shouldShowActionRequired = txEntity => {
    // Provider generally doesn't need to take action on an adjusted bill they created.
    // Keep it conservative: only show action-required for provider if it's overtime invoice and payment is pending.
    if (!isInvoiceAdjustment(txEntity)) return false;

    const lastTransition = txEntity?.attributes?.lastTransition;
    const actionableTransitions = ['transition/request-payment', 'transition/request-payment-to-accept-offer'];

    return actionableTransitions.includes(lastTransition);
  };

  if (loading) {
    return (
      <div className={css.eventDetailsContainer}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div>Loading transactions...</div>
          <IconSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={css.eventDetailsContainer}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ color: 'red' }}>Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={css.eventDetailsContainer}>
      <div className={css.eventDetailsContent}>
        <div className={css.eventDetailsHeader}>
          <div className={css.eventDetailsHeaderLeft}>
            <h1 className={css.eventDetailsTitle}>Event Transactions</h1>
            <div className={css.eventDetailsStatus}>
              <span className={css.statusBadgePending}>
                {/* <IconCollection icon="time_icon" /> */}
                {transactionCount} Transaction{transactionCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        <div className={css.transactionList}>
          {transactions.length === 0 ? (
            <div className={css.emptyState}>
              <div className={css.emptyStateIcon}>
                <IconCollection icon="time_icon" />
              </div>
              <div className={css.emptyStateText}>No transactions found</div>
            </div>
          ) : (
            <div className={css.transactionItems}>
              {transactions.map(item => {
                const txEntity = item?.transaction;
                const listingEntity = item?.listing;
                const listingPublicData = listingEntity?.attributes?.publicData || {};
                const eventTypeLabel = getEventTypeLabel(listingPublicData.eventType || '-');

                const isExpanded = expandedTransaction?.transactionId === item.transactionId;
                const status = getTransactionStatus(txEntity);
                const statusLabel = getTransactionStatusLabel(txEntity);

                return (
                  <div key={item.transactionId} className={css.transactionItem}>
                    <div
                      className={`${css.transactionSummary} ${isExpanded ? css.expanded : ''}`}
                      onClick={() => handleTransactionClick(item)}
                    >
                      <div className={css.transactionMain}>
                        <div className={css.transactionInfo}>
                          <div className={css.transactionTitle}>{listingEntity?.attributes?.title || 'N/A'}</div>
                          <div className={css.transactionMeta}>
                            <span className={css.transactionDate}>
                              <IconCollection icon="calendar_icon" />
                              {formatEventDate(listingPublicData.eventDetails)}
                            </span>
                            <span className={css.transactionTime}>
                              <IconCollection icon="clock_icon" />
                              {formatEventTime(listingPublicData.eventDetails)}
                            </span>
                          </div>
                          <span className={css.transactionId}>{item?.transactionId}</span>
                        </div>
                        <div className={css.transactionActions}>
                          <span
                            className={`${css.statusBadge} ${css[`statusBadge${status.charAt(0).toUpperCase() + status.slice(1)}`]}`}
                          >
                            {statusLabel}
                          </span>
                          <div className={css.expandIcon}>
                            <IconCollection icon={isExpanded ? 'chevron_up_icon' : 'chevron_down_icon'} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {isExpanded ? (
                      <div className={css.transactionDetails}>
                        <div className={css.transactionDetailsContent}>
                          <div className={css.eventDetailsGrid}>
                            <div className={css.eventDetailsInfo}>
                              <div className={css.eventInfoRow}>
                                <div className={css.eventInfoItem}>
                                  <div className={css.eventInfoLabel}>Event Date</div>
                                  <div className={css.eventInfoValue}>{formatEventDate(listingPublicData.eventDetails)}</div>
                                </div>
                                <div className={css.eventInfoItem}>
                                  <div className={css.eventInfoLabel}>Client</div>
                                  <div className={css.eventInfoValue}>{getCustomerName(item)}</div>
                                </div>
                                <div className={css.eventInfoItem}>
                                  <div className={css.eventInfoLabel}>Original Cost</div>
                                  <div className={css.eventInfoValue}>
                                    ${item.protectedData?.finalBid?.bidAmount
                                      ? (item.protectedData.finalBid.bidAmount / 100).toFixed(2)
                                      : '0.00'}
                                  </div>
                                </div>
                              </div>

                              <div className={css.eventInfoRow}>
                                <div className={css.eventInfoItem}>
                                  <div className={css.eventInfoLabel}>Event Time</div>
                                  <div className={css.eventInfoValue}>{formatEventTime(listingPublicData.eventDetails)}</div>
                                </div>
                                <div className={css.eventInfoItem}>
                                  <div className={css.eventInfoLabel}>Event Type</div>
                                  <div className={css.eventInfoValue}>{eventTypeLabel || 'N/A'}</div>
                                </div>
                                <div className={css.eventInfoItem}>
                                  <div className={css.eventInfoLabel}>Overtime Rate</div>
                                  <div className={css.eventInfoValue}>{getOvertimeRateLabel(item)}</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <EventTimeline transaction={txEntity} isProvider={true} />

                          <div className={css.sapcer}></div>

                          {shouldShowActionRequired(txEntity) ? (
                            <div className={css.eventTimelineItem}>
                              <div className={css.timelineIcon}>
                                <div className={css.timelineIconAction}>
                                  <IconCollection icon="attention_icon" />
                                </div>
                              </div>
                              <div className={css.actionContent}>
                                <div>
                                  <div className={css.timelineStatus}>Action Required</div>
                                  <div className={css.timelineDescription}>
                                    Customer has a pending payment for the adjusted bill
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : null}

                          <Button
                            className={css.viewAdjustedBillButton}
                            onClick={() => handleViewAdjustedBill(item.transactionId, txEntity)}
                          >
                            View Transaction
                          </Button>

                          <AdjustedBillActivity transaction={txEntity} invoiceType={getInvoiceType(txEntity)} />
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {shouldShowPagination ? (
          <PaginationLinks
            className={css.paginationContainer}
            pageName="ProviderDashBoardPage"
            pageSearchParams={{ ...queryParams, tab: 'event' }}
            pagination={pagination}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ProviderEventDetailsTab;
